import React, { useCallback, memo } from "react";
import { BlockStack, Button, InlineStack, List, Text, Icon } from "@shopify/polaris";
import { ExternalIcon, RefreshIcon, CheckCircleIcon } from "@shopify/polaris-icons";
import { openRemoteUrl } from "../../utils/openRemoteUrl";
import { PLACEMENT_OPTIONS } from "../../utils/themeEditorLinks";
import { APP_DISPLAY_NAME } from "../../config/appBranding";

function placementLabel(placementType) {
  const match = PLACEMENT_OPTIONS.find((p) => p.id === placementType);
  return match?.title || "your page";
}

function ThemeAppEmbedStatusBanner({
  themeLoading,
  themeRefreshing,
  embedInactive,
  themeCheckError,
  onRefreshThemes,
  getThemeEditorDeepLink,
  placementType = "product_page",
}) {
  const onThemeEditor = useCallback(() => {
    const url = getThemeEditorDeepLink?.();
    openRemoteUrl(url, { newContext: true });
    window.setTimeout(() => {
      onRefreshThemes?.();
    }, 2000);
  }, [getThemeEditorDeepLink, onRefreshThemes]);

  const refreshBusy = themeLoading || themeRefreshing;
  const inactive = embedInactive;
  const pageLabel = placementLabel(placementType);

  return (
    <div className={`age-embed-callout${inactive ? "" : " age-embed-callout--embed-on"}`}>
      <div
        className={`age-embed-callout__header${inactive ? "" : " age-embed-callout__header--active"}`}
      >
        <span className="age-embed-callout__headerIcon" aria-hidden>
          {inactive ? (
            <svg width="20" height="20" viewBox="0 0 20 20" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path
                d="M10 4L17 16H3L10 4Z"
                stroke="currentColor"
                strokeWidth="1.5"
                strokeLinejoin="round"
                fill="none"
              />
              <path d="M10 8v4" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
              <circle cx="10" cy="14" r="0.75" fill="currentColor" />
            </svg>
          ) : (
            <Icon source={CheckCircleIcon} tone="base" />
          )}
        </span>
        <Text as="p" variant="bodyMd" fontWeight="semibold">
          {inactive
            ? `${APP_DISPLAY_NAME} is not activated in your live theme`
            : `${APP_DISPLAY_NAME} is activated in your live theme`}
        </Text>
      </div>
      <div className="age-embed-callout__body">
        <BlockStack gap="400">
          <List type="bullet">
            <List.Item>
              <Text as="span" variant="bodyMd">
                {inactive ? (
                  <>
                    The free shipping bar is turned off for shoppers until you enable this app&apos;s embed in the
                    theme editor ({pageLabel}).
                  </>
                ) : (
                  <>
                    The free shipping bar is on for your shoppers. Open the theme editor to turn off this app&apos;s
                    embed or change where it appears.
                  </>
                )}
              </Text>
            </List.Item>
          </List>
          {themeCheckError ? (
            <Text as="p" variant="bodySm" tone="critical">
              Theme check: {themeCheckError}
            </Text>
          ) : null}
          <InlineStack gap="300" wrap>
            <Button variant="primary" icon={ExternalIcon} onClick={onThemeEditor} disabled={themeLoading}>
              {inactive ? "Enable app" : "Open theme editor"}
            </Button>
            <Button
              variant="secondary"
              icon={RefreshIcon}
              onClick={() => void onRefreshThemes?.()}
              loading={refreshBusy}
              disabled={refreshBusy}
            >
              Refresh status
            </Button>
          </InlineStack>
        </BlockStack>
      </div>
    </div>
  );
}

export default memo(ThemeAppEmbedStatusBanner);
